import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Mic, 
  MicOff, 
  Users, 
  Heart, 
  Scale, 
  HeartHandshake,
  Wifi,
  WifiOff,
  AlertCircle,
  User,
  Bot,
  Activity,
  Clock,
  CheckCircle,
  Loader,
  AlertTriangle,
  Shield,
  Phone,
  Zap,
  Settings
} from 'lucide-react';
import { cn } from '@/lib/utils';
import useAudioStream from '@/hooks/useAudioStream';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TriageDashboard = () => {
  const { selectedIndustry } = useOutletContext() || { selectedIndustry: 'All' };
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [sessionDuration, setSessionDuration] = useState(0);
  
  const transcriptEndRef = useRef(null);

  const {
    connect,
    disconnect,
    startRecording,
    stopRecording,
    clearTranscript,
    isConnected,
    isRecording,
    transcript,
    sessionId,
    error: audioError,
    toolResults,
    isThinking,
    industryType
  } = useAudioStream();

  const industryIcons = {
    Medical: Heart,
    Legal: Scale,
    AgedCare: HeartHandshake
  };

  const industryColors = {
    Medical: '#ef4444',
    Legal: '#3b82f6',
    AgedCare: '#10b981'
  };

  const industryBadgeColors = {
    Medical: 'bg-red-500/20 text-red-400 border-red-500/30',
    Legal: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    AgedCare: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
  };

  // Timer effect for session duration
  useEffect(() => {
    let interval;
    if (isConnected && sessionStartTime) {
      interval = setInterval(() => {
        setSessionDuration(Math.floor((Date.now() - sessionStartTime) / 1000));
      }, 1000);
    } else {
      setSessionDuration(0);
    }
    return () => clearInterval(interval);
  }, [isConnected, sessionStartTime]);

  // Format duration as MM:SS
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  useEffect(() => {
    // Auto-scroll to bottom when new transcript messages arrive
    if (transcriptEndRef.current) {
      transcriptEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [transcript]);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/tenants`);
      let tenantsData = response.data || [];
      
      // Filter by selected industry if not 'All'
      if (selectedIndustry !== 'All') {
        tenantsData = tenantsData.filter(tenant => tenant.industry_type === selectedIndustry);
      }
      
      setTenants(tenantsData);
      setError(null);
    } catch (err) {
      console.error('Error fetching tenants:', err);
      setError('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  // Refetch when industry filter changes
  useEffect(() => {
    fetchTenants();
  }, [selectedIndustry]);

  const handleTenantSelect = (tenantId) => {
    const tenant = tenants.find(t => t.tenant_id === tenantId);
    setSelectedTenant(tenant);
    
    // Disconnect previous session if any
    if (isConnected) {
      disconnect();
    }
    
    // Clear previous data
    clearTranscript();
    setSessionStartTime(null);
  };

  const handleConnect = () => {
    if (!selectedTenant) return;
    
    if (isConnected) {
      disconnect();
      setSessionStartTime(null);
    } else {
      connect(selectedTenant.tenant_id);
      setSessionStartTime(Date.now());
    }
  };

  const handleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const getConnectionStatus = () => {
    if (!selectedTenant) return { text: 'Select Tenant', color: 'text-slate-400', icon: Users };
    if (!isConnected) return { text: 'Disconnected', color: 'text-red-400', icon: WifiOff };
    if (isRecording) return { text: 'Recording', color: 'text-emerald-400', icon: Mic };
    return { text: 'Connected', color: 'text-cyan-400', icon: Wifi };
  };

  // Get latest tool result by name
  const getLatestToolResult = (toolName) => {
    return toolResults.filter(t => t.tool_name === toolName).pop();
  };

  // Render industry-specific panel
  const renderIndustryPanel = () => {
    if (!industryType || !isConnected) return null;

    switch (industryType) {
      case 'Medical':
        return (
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
              <Heart className="w-4 h-4 mr-2 text-red-400" />
              Medical Assessment
            </h4>
            
            {/* Triage Assessment */}
            {getLatestToolResult('assess_symptom_severity') && (
              <div className="industry-card">
                <div className="industry-card-header text-red-400">Triage Assessment</div>
                <div className="space-y-2 text-sm">
                  {(() => {
                    const result = getLatestToolResult('assess_symptom_severity').result;
                    return (
                      <>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Severity:</span>
                          <Badge className={result.severity === 'URGENT' ? 'alert-emergency text-xs' : 'alert-normal text-xs'}>
                            {result.severity || 'NORMAL'}
                          </Badge>
                        </div>
                        {result.pain_scale && (
                          <div className="flex justify-between">
                            <span className="text-slate-400">Pain Scale:</span>
                            <span className="text-white font-medium">{result.pain_scale}/10</span>
                          </div>
                        )}
                        {result.flags && result.flags.length > 0 && (
                          <div className="mt-2">
                            <div className="text-xs text-slate-400 mb-1">Flags:</div>
                            {result.flags.map((flag, idx) => (
                              <Badge key={idx} className="text-xs mr-1 mb-1 bg-amber-500/20 text-amber-400">
                                {flag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Allergy Profile */}
            {getLatestToolResult('check_allergies') && (
              <div className="industry-card">
                <div className="industry-card-header text-red-400">Allergy Profile</div>
                <div className="text-sm">
                  {(() => {
                    const result = getLatestToolResult('check_allergies').result;
                    return (
                      <div className="space-y-1">
                        {result.allergies && result.allergies.length > 0 ? (
                          result.allergies.map((allergy, idx) => (
                            <Badge key={idx} className="text-xs mr-1 bg-red-500/20 text-red-400">
                              {allergy}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-emerald-400">No known allergies</span>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Emergency Status */}
            {getLatestToolResult('detect_emergency') && (
              <div className={cn("industry-card", getLatestToolResult('detect_emergency').result.is_emergency ? 'alert-emergency' : '')}>
                <div className="industry-card-header text-red-400">Emergency Status</div>
                <div className="text-sm">
                  {getLatestToolResult('detect_emergency').result.is_emergency ? (
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                      <span className="text-red-400 font-semibold">EMERGENCY DETECTED</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span className="text-emerald-400">No emergency</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );

      case 'Legal':
        return (
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
              <Scale className="w-4 h-4 mr-2 text-blue-400" />
              Legal Analysis
            </h4>
            
            {/* Case Classification */}
            {getLatestToolResult('classify_case_type') && (
              <div className="industry-card">
                <div className="industry-card-header text-blue-400">Case Classification</div>
                <div className="text-sm">
                  {(() => {
                    const result = getLatestToolResult('classify_case_type').result;
                    return (
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-slate-400">Type:</span>
                          <Badge className="text-xs bg-blue-500/20 text-blue-400">
                            {result.case_type || 'General'}
                          </Badge>
                        </div>
                        {result.complexity && (
                          <div className="flex justify-between">
                            <span className="text-slate-400">Complexity:</span>
                            <span className="text-white">{result.complexity}</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Conflict Check */}
            {getLatestToolResult('check_conflict_of_interest') && (
              <div className={cn("industry-card", getLatestToolResult('check_conflict_of_interest').result.has_conflict ? 'alert-emergency' : '')}>
                <div className="industry-card-header text-blue-400">Conflict Check</div>
                <div className="text-sm">
                  {getLatestToolResult('check_conflict_of_interest').result.has_conflict ? (
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                      <span className="text-red-400 font-semibold">CONFLICT DETECTED</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Shield className="w-4 h-4 text-emerald-400" />
                      <span className="text-emerald-400">No conflicts</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Statute Alert */}
            {getLatestToolResult('assess_statute_of_limitations') && (
              <div className="industry-card">
                <div className="industry-card-header text-blue-400">Statute Alert</div>
                <div className="text-sm">
                  {(() => {
                    const result = getLatestToolResult('assess_statute_of_limitations').result;
                    return (
                      <div className="space-y-1">
                        {result.expires_soon ? (
                          <Badge className="text-xs bg-amber-500/20 text-amber-400">
                            Expires: {result.expiry_date}
                          </Badge>
                        ) : (
                          <span className="text-emerald-400">Within limits</span>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        );

      case 'AgedCare':
        return (
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
              <HeartHandshake className="w-4 h-4 mr-2 text-emerald-400" />
              Aged Care Assessment
            </h4>
            
            {/* Mobility Risk */}
            {getLatestToolResult('assess_mobility_risk') && (
              <div className="industry-card">
                <div className="industry-card-header text-emerald-400">Mobility Risk</div>
                <div className="text-sm">
                  {(() => {
                    const result = getLatestToolResult('assess_mobility_risk').result;
                    return (
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-slate-400">Risk Level:</span>
                          <Badge className={cn("text-xs", 
                            result.risk_level === 'HIGH' ? 'bg-red-500/20 text-red-400' : 
                            result.risk_level === 'MEDIUM' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-emerald-500/20 text-emerald-400'
                          )}>
                            {result.risk_level || 'LOW'}
                          </Badge>
                        </div>
                        {result.assistive_devices && (
                          <div className="text-xs text-slate-300">
                            Devices: {result.assistive_devices.join(', ')}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* ACFI Score */}
            {getLatestToolResult('acfi_assessment') && (
              <div className="industry-card">
                <div className="industry-card-header text-emerald-400">ACFI Score</div>
                <div className="text-sm">
                  {(() => {
                    const result = getLatestToolResult('acfi_assessment').result;
                    return (
                      <div className="space-y-1">
                        {result.total_score && (
                          <div className="flex justify-between">
                            <span className="text-slate-400">Total Score:</span>
                            <span className="text-white font-medium">{result.total_score}</span>
                          </div>
                        )}
                        {result.domains && Object.entries(result.domains).map(([domain, score]) => (
                          <div key={domain} className="flex justify-between text-xs">
                            <span className="text-slate-400 capitalize">{domain}:</span>
                            <span className="text-slate-300">{score}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Kinship Network */}
            {getLatestToolResult('record_kinship_details') && (
              <div className="industry-card">
                <div className="industry-card-header text-emerald-400">Kinship Network</div>
                <div className="text-sm">
                  {(() => {
                    const result = getLatestToolResult('record_kinship_details').result;
                    return (
                      <div className="space-y-1">
                        {result.primary_contact && (
                          <div className="text-xs">
                            <span className="text-slate-400">Primary:</span>
                            <span className="text-white ml-2">{result.primary_contact.name}</span>
                          </div>
                        )}
                        {result.emergency_contact && (
                          <div className="text-xs">
                            <span className="text-slate-400">Emergency:</span>
                            <span className="text-white ml-2">{result.emergency_contact.name}</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="text-center text-slate-400 mt-8">
            <Settings className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Industry panel will appear here</p>
          </div>
        );
    }
  };

  // Render tool status feed
  const renderToolStatusFeed = () => {
    if (toolResults.length === 0) return null;

    return (
      <div className="mt-6 space-y-2">
        <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
          <Zap className="w-4 h-4 mr-2 text-cyan-400" />
          Tool Executions
        </h4>
        <div className="space-y-1 max-h-32 overflow-y-auto">
          {toolResults.slice(-5).map((tool) => (
            <div key={tool.id} className="flex items-center space-x-3 text-xs py-1">
              <CheckCircle className="w-3 h-3 text-emerald-400 flex-shrink-0" />
              <span className="text-slate-300 truncate flex-1">{tool.tool_name}</span>
              <span className="text-slate-500">{new Date(tool.timestamp).toLocaleTimeString()}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Generate audio visualizer bars
  const renderAudioVisualizer = () => {
    return (
      <div className="flex items-end justify-center space-x-1 h-20 px-4 mb-4">
        {Array.from({ length: 40 }, (_, i) => (
          <div
            key={i}
            className={cn(
              "audio-bar",
              isRecording ? "" : "inactive"
            )}
            style={{
              animationDelay: `${i * 0.05}s`,
              height: isRecording ? `${Math.random() * 60 + 4}px` : '4px'
            }}
          />
        ))}
      </div>
    );
  };

  const status = getConnectionStatus();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Error Alert */}
      {(error || audioError) && (
        <Alert className="bg-red-500/10 border-red-500/20 text-red-400">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error || audioError}
          </AlertDescription>
        </Alert>
      )}

      {/* TOP CONTROL BAR */}
      <Card className="glass-card">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-center">
            {/* Tenant Selector */}
            <div className="space-y-2">
              <label className="text-xs text-slate-400 font-medium uppercase tracking-wide">Tenant</label>
              <Select onValueChange={handleTenantSelect} data-testid="tenant-selector">
                <SelectTrigger className="bg-slate-800/50 border-slate-600 text-white text-sm">
                  <SelectValue placeholder="Choose tenant..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  {tenants.map((tenant) => {
                    const IndustryIcon = industryIcons[tenant.industry_type] || Users;
                    return (
                      <SelectItem key={tenant.tenant_id} value={tenant.tenant_id} className="text-white hover:bg-slate-700">
                        <div className="flex items-center space-x-2">
                          <IndustryIcon className="w-4 h-4" style={{ color: industryColors[tenant.industry_type] }} />
                          <span>{tenant.name}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Connection Controls */}
            <div className="space-y-2">
              <label className="text-xs text-slate-400 font-medium uppercase tracking-wide">Connection</label>
              <div className="flex space-x-2">
                <Button
                  onClick={handleConnect}
                  disabled={!selectedTenant}
                  variant={isConnected ? "destructive" : "default"}
                  size="sm"
                  className={cn(
                    "flex-1",
                    isConnected 
                      ? "bg-red-500 hover:bg-red-600 text-white" 
                      : "bg-cyan-500 hover:bg-cyan-600 text-white"
                  )}
                  data-testid="connect-button"
                >
                  <status.icon className="w-4 h-4 mr-2" />
                  {isConnected ? 'Disconnect' : 'Connect'}
                </Button>
                
                {isConnected && (
                  <Button
                    onClick={handleRecording}
                    size="sm"
                    className={cn(
                      "px-3",
                      isRecording
                        ? "bg-red-500 hover:bg-red-600 text-white pulse-glow"
                        : "bg-emerald-500 hover:bg-emerald-600 text-white"
                    )}
                    data-testid="record-button"
                  >
                    {isRecording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </Button>
                )}
              </div>
            </div>

            {/* Session Info */}
            <div className="space-y-2">
              <label className="text-xs text-slate-400 font-medium uppercase tracking-wide">Session</label>
              <div className="space-y-1">
                {sessionId ? (
                  <>
                    <div className="text-xs text-slate-300">ID: {sessionId.slice(0, 8)}...</div>
                    <div className="flex items-center space-x-2 text-xs text-slate-300">
                      <Clock className="w-3 h-3" />
                      <span>{formatDuration(sessionDuration)}</span>
                    </div>
                  </>
                ) : (
                  <div className="text-xs text-slate-500">Not connected</div>
                )}
              </div>
            </div>

            {/* Industry Badge */}
            <div className="space-y-2">
              <label className="text-xs text-slate-400 font-medium uppercase tracking-wide">Industry</label>
              <div>
                {selectedTenant ? (
                  <Badge className={cn("text-xs", industryBadgeColors[selectedTenant.industry_type] || 'bg-slate-500/20 text-slate-400')}>
                    {selectedTenant.industry_type}
                    {industryType && industryType !== selectedTenant.industry_type && (
                      <span className="ml-1">→ {industryType}</span>
                    )}
                  </Badge>
                ) : (
                  <Badge className="text-xs bg-slate-500/20 text-slate-400">
                    Select tenant
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* MAIN INTERFACE */}
      {isConnected && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT: Audio Visualizer + Transcript */}
          <div className="lg:col-span-2 space-y-6">
            {/* Audio Visualizer */}
            <Card className="glass-card">
              <CardContent className="p-6">
                <div className="text-center space-y-4">
                  <h3 className="text-lg font-semibold text-white flex items-center justify-center">
                    <Activity className="w-5 h-5 mr-2 text-emerald-400" />
                    Audio Stream
                    {isRecording && <span className="ml-2 text-red-400 animate-pulse">● REC</span>}
                  </h3>
                  
                  {renderAudioVisualizer()}
                  
                  <div className="flex justify-center space-x-4">
                    <div className={cn("text-sm font-medium", status.color)}>
                      {status.text}
                    </div>
                    {isRecording && (
                      <div className="text-sm text-slate-400">
                        Speaking...
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Dual-Pane Transcript */}
            <Card className="glass-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Live Conversation</h3>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="border-emerald-500/30 text-emerald-400 text-xs">
                      {transcript.length} messages
                    </Badge>
                    <Button
                      onClick={clearTranscript}
                      variant="ghost"
                      size="sm"
                      className="text-slate-400 hover:text-white text-xs px-2 py-1"
                      data-testid="clear-transcript-button"
                    >
                      Clear
                    </Button>
                  </div>
                </div>

                <div className="h-80 overflow-y-auto space-y-3 pr-2" data-testid="transcript-container">
                  {transcript.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-slate-400">
                      <div className="text-center">
                        <Bot className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p className="text-sm">Conversation will appear here...</p>
                        <p className="text-xs">Start recording to begin</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      {transcript.map((message) => {
                        // Handle different message types
                        if (message.type === 'tool_result') {
                          return (
                            <div key={message.id} className="flex justify-center">
                              <div className="bg-amber-500/10 border border-amber-500/30 text-amber-400 px-3 py-2 rounded-lg max-w-xs text-center">
                                <div className="text-xs font-mono mb-1">TOOL: {message.tool_name}</div>
                                <div className="text-xs">
                                  {typeof message.tool_result === 'object' 
                                    ? Object.keys(message.tool_result).join(', ')
                                    : String(message.tool_result).slice(0, 50) + '...'
                                  }
                                </div>
                              </div>
                            </div>
                          );
                        }

                        // Regular chat messages
                        return (
                          <div
                            key={message.id}
                            className={cn(
                              "flex",
                              message.speaker === 'agent' ? 'justify-start' : 'justify-end'
                            )}
                          >
                            <div
                              className={cn(
                                "max-w-sm px-4 py-3 rounded-lg",
                                message.speaker === 'agent'
                                  ? 'bg-slate-700/50 text-white'
                                  : 'bg-emerald-500/20 border border-emerald-500/30 text-white'
                              )}
                            >
                              <div className="flex items-center space-x-2 mb-1">
                                {message.speaker === 'agent' ? (
                                  <Bot className="w-4 h-4 text-cyan-400" />
                                ) : (
                                  <User className="w-4 h-4 text-emerald-400" />
                                )}
                                <span className="text-xs opacity-75 capitalize">
                                  {message.speaker}
                                </span>
                                <span className="text-xs opacity-50">
                                  {new Date(message.timestamp).toLocaleTimeString()}
                                </span>
                              </div>
                              <p className="text-sm">{message.text}</p>
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Thinking indicator */}
                      {isThinking && (
                        <div className="flex justify-start">
                          <div className="bg-slate-700/50 px-4 py-3 rounded-lg max-w-xs">
                            <div className="flex items-center space-x-2 mb-1">
                              <Bot className="w-4 h-4 text-cyan-400" />
                              <span className="text-xs opacity-75">Agent</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <span className="text-sm text-slate-300">Thinking</span>
                              <div className="thinking-dot"></div>
                              <div className="thinking-dot"></div>
                              <div className="thinking-dot"></div>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  <div ref={transcriptEndRef} />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* RIGHT: Industry Panel + Tool Status */}
          <div className="space-y-6">
            {/* Industry-Aware Panel */}
            <Card className="glass-card">
              <CardContent className="p-4">
                {renderIndustryPanel()}
                {renderToolStatusFeed()}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Help Section */}
      {!isConnected && (
        <Card className="glass-card opacity-80">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Real-time Voice Triage</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <span className="text-emerald-400 font-bold">1</span>
                </div>
                <p className="text-sm text-white font-medium">Select Tenant</p>
                <p className="text-xs text-slate-400">Choose healthcare, legal, or aged care organization</p>
              </div>
              <div className="space-y-2">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <span className="text-emerald-400 font-bold">2</span>
                </div>
                <p className="text-sm text-white font-medium">Connect & Record</p>
                <p className="text-xs text-slate-400">Establish session, then start voice recording</p>
              </div>
              <div className="space-y-2">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <span className="text-emerald-400 font-bold">3</span>
                </div>
                <p className="text-sm text-white font-medium">Monitor Assessment</p>
                <p className="text-xs text-slate-400">Watch real-time analysis and industry-specific insights</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TriageDashboard;